import { useCallback, useEffect, useState } from 'react'
import { chores, kids, store, type Assignment, type Item } from '../lib/chores'

/**
 * The child's side.
 *
 * Deliberately three things and no navigation to speak of: what to do, what you
 * have, and what you can get. The audience is seven.
 */
export default function KidHome({ name }: { name: string }) {
  const [mine, setMine] = useState<Assignment[]>([])
  const [items, setItems] = useState<Item[]>([])
  const [balance, setBalance] = useState({ points: 0, coins: 0 })
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [a, s, l] = await Promise.all([chores.assignments(), store.items(), kids.ledger()])
    setMine(a.assignments)
    setItems(s.items)
    setBalance(l.balance)
  }, [])

  useEffect(() => { void load() }, [load])

  async function run(fn: () => Promise<unknown>, ok: string) {
    setBusy(true); setMessage(null)
    try { await fn(); await load(); setMessage(ok) }
    catch (e) { setMessage((e as Error).message) }
    finally { setBusy(false) }
  }

  const todo = mine.filter((a) => a.status === 'pending')
  const waiting = mine.filter((a) => a.status === 'submitted')

  return (
    <>
      <section className="card">
        <h1>Hi {name}</h1>
        <div className="stats">
          <button><b>{balance.points}</b><span>points</span></button>
          <button><b>{balance.coins}</b><span>coins</span></button>
        </div>
        {message && <p className="muted">{message}</p>}
      </section>

      <section className="card">
        <h2>To do</h2>
        <ul className="list">
          {todo.length === 0 && <li className="muted">Nothing right now.</li>}
          {todo.map((a) => (
            <li key={a.id}>
              <div>
                <strong>{a.name}</strong>
                <div className="muted small">{a.points} points · due {a.due_on}</div>
                {a.details && <div className="muted small">{a.details}</div>}
              </div>
              <button className="primary" disabled={busy}
                onClick={() => run(() => chores.submit(a.id), 'Sent to your parent.')}>
                Done!
              </button>
            </li>
          ))}
        </ul>

        {waiting.length > 0 && (
          <>
            <h3>Waiting to be checked</h3>
            <ul className="list">
              {waiting.map((a) => (
                <li key={a.id}><div><strong>{a.name}</strong>
                  <div className="muted small">{a.points} points when it is approved</div></div></li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="card">
        <h2>The store</h2>
        <ul className="list">
          {items.length === 0 && <li className="muted">Nothing in the store yet.</li>}
          {items.map((i) => {
            const affordable = balance.points >= i.points_cost
            return (
              <li key={i.id}>
                <div>
                  <strong>{i.name}</strong>
                  <div className="muted small">
                    {i.points_cost} points
                    {/* Say how far off rather than just disabling the button. */}
                    {!affordable && ` · ${i.points_cost - balance.points} more to go`}
                  </div>
                </div>
                <button disabled={busy || !affordable}
                  onClick={() => run(() => store.redeem(i.id), 'Asked your parent for it.')}>
                  Get it
                </button>
              </li>
            )
          })}
        </ul>
      </section>
    </>
  )
}
